import { COMBO_COUNT } from '../range/combos';
/**
 * Build a Vose alias table over the LIVE combos of a weighted range (weight > epsilon).
 * Assumes card removal has already zeroed dead combos. Returns a table with n = 0 when the range is
 * empty; callers must guard (sampleAlias throws on an empty table rather than sample a dead combo).
 */
export const buildAlias = (range, epsilon = 1e-9) => {
    const w = range.weights;
    // Collect live columns and their total weight.
    let n = 0;
    let total = 0;
    for (let i = 0; i < COMBO_COUNT; i++) {
        if (w[i] > epsilon) {
            n += 1;
            total += w[i];
        }
    }
    const indices = new Int32Array(n);
    const prob = new Float64Array(n);
    const alias = new Int32Array(n);
    if (n === 0)
        return { indices, prob, alias, n, totalWeight: 0 };
    // scaled[k] = p_k · n, where p_k is the k-th live combo's normalized weight. Mean of scaled is 1.
    const scaled = new Float64Array(n);
    let k = 0;
    for (let i = 0; i < COMBO_COUNT; i++) {
        if (w[i] > epsilon) {
            indices[k] = i;
            scaled[k] = (w[i] / total) * n;
            k += 1;
        }
    }
    // Partition columns into under-full (<1) and over-full (≥1) work stacks.
    const small = new Int32Array(n);
    const large = new Int32Array(n);
    let sTop = 0;
    let lTop = 0;
    for (let j = 0; j < n; j++) {
        if (scaled[j] < 1)
            small[sTop++] = j;
        else
            large[lTop++] = j;
    }
    // Pair a small column with a large donor: the small column keeps its own mass and borrows the rest
    // from the donor. Re-classify the donor by its remaining mass.
    while (sTop > 0 && lTop > 0) {
        const less = small[--sTop];
        const more = large[--lTop];
        prob[less] = scaled[less];
        alias[less] = more;
        scaled[more] = scaled[more] + scaled[less] - 1;
        if (scaled[more] < 1)
            small[sTop++] = more;
        else
            large[lTop++] = more;
    }
    // Drain residuals. Anything left is a column whose mass is ~1 (up to float drift); pin it to 1 so it
    // always accepts itself and never dereferences a stale alias.
    while (lTop > 0)
        prob[large[--lTop]] = 1;
    while (sTop > 0)
        prob[small[--sTop]] = 1;
    return { indices, prob, alias, n, totalWeight: total };
};
/**
 * Draw one combo index from an alias table in O(1). Consumes two Rng draws (column, then accept/alias
 * coin). Throws on an empty table - sampling a range with no live combos is a caller bug, not a value.
 */
export const sampleAlias = (t, rng) => {
    if (t.n === 0)
        throw new RangeError('sampleAlias: empty alias table (no live combos)');
    const col = (rng.next() * t.n) | 0; // floor into [0, n)
    const c = col < t.n ? col : t.n - 1; // guard the rng.next()===1 edge (mathematically impossible, but cheap)
    // accept → this column's combo; reject → the ALIAS column's combo. alias[] holds a column index,
    // so it must be mapped back through indices[] to a combo index (returning alias[c] raw is a bug -
    // it only ever agrees with the right answer under uniform weights, where the reject branch is dead).
    return rng.next() < t.prob[c] ? t.indices[c] : t.indices[t.alias[c]];
};
//# sourceMappingURL=alias.js.map